import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import io, { type Socket } from 'socket.io-client';

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  activeScanId: string | null;
  joinScanRoom: (scanId: string) => void;
  leaveScanRoom: (scanId: string) => void;
  leaveCurrentScanRoom: () => void;
  addEventListener: (event: string, callback: (...args: any[]) => void) => () => void;
  removeEventListener: (event: string, callback: (...args: any[]) => void) => void;
  emit: (event: string, data: any) => void;
}

interface SocketProviderProps {
  children: React.ReactNode;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [activeScanId, setActiveScanId] = useState<string | null>(null);

  useEffect(() => {
    // Create socket connection - use BACKEND_BASE_URL (without /api) for Socket.IO
    const socketUrl: string | undefined = import.meta.env.VITE_BACKEND_BASE_URL || import.meta.env.VITE_API_URL;
    const newSocket: Socket = io(socketUrl as string, {
      transports: ['websocket', 'polling'],
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);
      toast.success('Connected to scanner', { autoClose: 2000 });
    });

    newSocket.on('disconnect', (reason: string) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      toast.warning('Disconnected from scanner', { autoClose: 2000 });
    });

    newSocket.on('connect_error', (error: Error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
      toast.error('Connection failed', { autoClose: 3000 });
    });

    // Scan events
    newSocket.on('scanStarted', (data: any) => {
      console.log('Scan started:', data);
      toast.info(`Scan ${data.scanId} started`, { autoClose: 3000 });
    });

    newSocket.on('phaseChanged', (data: any) => {
      console.log('Scan phase changed:', data);
      toast.info(`Phase: ${data.phase}`, { autoClose: 2000 });
    });

    newSocket.on('requestCompleted', (data: any) => {
      console.log('Request completed:', data);

      // Show vulnerability notifications
      if (data.vulnerability) {
        const severity: string = data.vulnerability.severity?.toLowerCase() || 'medium';
        const toastMethod = severity === 'high' ? toast.error : severity === 'medium' ? toast.warning : toast.info;

        toastMethod(`${data.vulnerability.type} vulnerability found`, { autoClose: 5000 });
      }
    });

    newSocket.on('vulnerabilityFound', (data: any) => {
      console.log('Vulnerability found:', data);

      const severity: string = data.vulnerability.severity?.toLowerCase() || 'medium';
      const toastMethod = severity === 'high' ? toast.error : severity === 'medium' ? toast.warning : toast.info;

      toastMethod(`\u{1F6A8} ${data.vulnerability.type}: ${data.vulnerability.title}`, {
        autoClose: 8000,
        onClick: () => {
          // Could navigate to vulnerability details
          console.log('Navigate to vulnerability:', data.vulnerability);
        },
      });
    });

    newSocket.on('scanCompleted', (data: any) => {
      console.log('Scan completed:', data);

      const vulnerabilityCount: number = data.stats?.vulnerabilitiesFound || 0;
      const message: string =
        vulnerabilityCount > 0
          ? `Scan completed: ${vulnerabilityCount} vulnerabilities found`
          : 'Scan completed: No vulnerabilities found';
      const toastMethod = vulnerabilityCount > 0 ? toast.warning : toast.success;
      toastMethod(message, { autoClose: 5000 });
    });

    newSocket.on('scanError', (data: any) => {
      console.error('Scan error:', data);
      toast.error(`Scan failed: ${data.error}`, { autoClose: 5000 });
    });

    newSocket.on('scanStopped', (data: any) => {
      console.log('Scan stopped:', data);
      toast.warning('Scan stopped by user', { autoClose: 3000 });
    });

    // Worker events
    newSocket.on('workerError', (data: any) => {
      console.error('Worker error:', data);
      toast.error(`Worker ${data.workerId} error: ${data.error}`, { autoClose: 5000 });
    });

    newSocket.on('workProgress', (data: any) => {
      console.log('Work progress:', data);
      // This could be used to update progress bars
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up socket connection');
      newSocket.disconnect();
    };
  }, []);

  // Helper functions
  const joinScanRoom = useCallback(
    (scanId: string): void => {
      if (socket && scanId) {
        socket.emit('joinScan', scanId);
        setActiveScanId(scanId);
        console.log(`Joined scan room: ${scanId}`);
      }
    },
    [socket],
  );

  const leaveScanRoom = useCallback(
    (scanId: string): void => {
      if (socket && scanId) {
        socket.emit('leaveScan', scanId);
        setActiveScanId((prev) => (prev === scanId ? null : prev));
        console.log(`Left scan room: ${scanId}`);
      }
    },
    [socket],
  );

  const leaveCurrentScanRoom = useCallback((): void => {
    if (activeScanId) {
      leaveScanRoom(activeScanId);
    }
  }, [activeScanId, leaveScanRoom]);

  // Custom event listeners
  const addEventListener = useCallback(
    (event: string, callback: (...args: any[]) => void): (() => void) => {
      if (socket) {
        socket.on(event, callback);
        return () => socket.off(event, callback);
      }
      return () => {};
    },
    [socket],
  );

  const removeEventListener = useCallback(
    (event: string, callback: (...args: any[]) => void): void => {
      if (socket) {
        socket.off(event, callback);
      }
    },
    [socket],
  );

  const emit = useCallback(
    (event: string, data: any): void => {
      if (socket) {
        socket.emit(event, data);
      }
    },
    [socket],
  );

  const value = useMemo<SocketContextValue>(
    () => ({
      socket,
      isConnected,
      activeScanId,
      joinScanRoom,
      leaveScanRoom,
      leaveCurrentScanRoom,
      addEventListener,
      removeEventListener,
      emit,
    }),
    [
      socket,
      isConnected,
      activeScanId,
      joinScanRoom,
      leaveScanRoom,
      leaveCurrentScanRoom,
      addEventListener,
      removeEventListener,
      emit,
    ],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
